/* eslint-disable @typescript-eslint/naming-convention */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { badgeColorCode, BadgeOptions } from '../../../components/badge/badge.interface';
import { NotesOptions, ParamKey } from '../../../components/notes/notes.interface';
import { CustomersService } from '../../../core/services/customers/customers.service';
import { CustomFieldGroupService } from '../../../core/services/custom_fields_group/custom_fields_group.service';
import { DataCollectorService } from '../../../core/services/data_collector_services/data_collector_service.service';
import { LocalisationService } from '../../../core/services/localisation/zuper.localisation.service';
import { UtilityService } from '../../../core/services/utility_functions/utility_functions.service';
import { forkJoin } from 'rxjs';
import { NotesService } from 'app/core/services/notes/notes.service';
import { moduleKey } from 'app/core/constants/constants';
import { CustomFieldsOptions } from '../../../components/custom-fields/custom-fields.interface';

@Component({
  selector: 'customer-summary-preview',
  templateUrl: './customer-summary-preview.component.html',
  styleUrls: ['./customer-summary-preview.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection:ChangeDetectionStrategy?.OnPush
})
export class CustomerSummaryPreviewComponent implements OnInit, OnChanges {
  @Input() customerId: any = '';
  @Output() eventEmitter: EventEmitter<any> = new EventEmitter();

  object = Object;
  customFieldGroups: any = {};
  masterCustomFields: any[] = [];
  loaded: boolean = false;
  customerDetails: any = {};
  tabs: any[] = [{
    displayName: 'Primary Details',
    displayKey: 'text.primary_details',
    dataKey: 'PrimaryDetails',
    rel: 'customer_primary_details'
  }, {
    displayName: 'Notes',
    displayKey: 'text.notes',
    dataKey: 'Notes',
    rel: 'customer_notes'
  }];
  viewMode: string = 'PrimaryDetails';
  notesOptions: NotesOptions = null;
  notesCount: number = 0;
  isOrganizationAccessible: boolean;

  constructor(
    private dataCollectorService: DataCollectorService,
    private localisationService: LocalisationService,
    private customFieldServie: CustomFieldGroupService,
    private customerService: CustomersService,
    private notesService: NotesService,
    private utilityService: UtilityService,
    private changeDetectorRef: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    this.localisationService.setDefaultLanguage();
    this.isOrganizationAccessible = this.utilityService.hasModuleAccess('ORGANIZATION');
    this.getCustomerDetails();
    this.changeDetectorRef.markForCheck();
  }

  changeViewMode(mode: string): void {
    this.viewMode = mode;
    this.changeDetectorRef.markForCheck();
  }

  getBadgeOptions(): BadgeOptions {
    const badgeOption: BadgeOptions = {
      type: 'PRIMARY',
      displayKey: '',
      displayName: this.notesCount,
    };
    return badgeOption;
  }

  closeDrawer(): void {
    this.eventEmitter.emit({ drawerClose: true });
    this.changeDetectorRef.markForCheck();
  }

  getCustomerDetails(): void {
    this.loaded = false;
    forkJoin([
      this.dataCollectorService.getData(this.customerService.getCustomerUrl(this.customerId)),
      this.dataCollectorService.getData(this.customFieldServie.getModuleCustomFieldsUrl('CUSTOMER'))])
      .subscribe(
        ([customerDetails, customFields]: any) => {
          this.customerDetails = customerDetails.data;
          this.masterCustomFields = customFields.data;
          this.loaded = true;
          this.getCustomFieldGroups();
          this.notesOptions = this.getNotesOptions();
          this.changeDetectorRef.markForCheck();
        }, (_error: any) => {
          this.loaded = true;
          this.changeDetectorRef.markForCheck();
        });
  }

  getNotesOptions(): NotesOptions {
    const notesOptions = {
      config: {
        attachments: {
          isEnabled: true,
          attachmentType: 'JOB_NOTE'
        },
        sortOptions: {
          isEnabled: true
        },
        showNotifyUsers: false,
        showUserProfile: true,
        enableNewNote: true,
        enableNewAssociation: false,
        enablePrivacy: true,
        editorOptions: {
          quickCreate: true
        },
        renderSmallScreen: true,
        initUponFocus: true
      },
      dataUrl: this.notesService.notesUrl,
      module: moduleKey.CUSTOMER,
      moduleUID: this.customerDetails.customer_uid,
      params: {
        'sort': 'DESC',
        'sort_by': 'created_at'
      },
      defaultAssociatedModuleDetails: this.customerDetails
    };
    notesOptions.params['filter.'+ParamKey.CUSTOMER] = this.customerDetails.customer_uid;
    return notesOptions;
  }

  getCustomFieldGroups(): void {
    this.customFieldGroups = this.customFieldServie.getCustomFieldGroups(this.masterCustomFields, this.customerDetails.custom_fields);
    this.changeDetectorRef.markForCheck();
  }

  getRedirectLink(): string {
    return this.utilityService.getLink('CUSTOMER', this.customerDetails.customer_uid);
  }

  getcustomFieldOptions(data: any): CustomFieldsOptions {
    return {
      data: data,
      displayInColumn: 1,
      extraClass: 'px-0',
      labelAlign: 'Left'
    };
  }

  notesEventHandler(event: any): void {
    if (event && !this.utilityService.isEmptyOrUndefined(event.notesCount)) {
      this.notesCount = event.notesCount;
    }
  }

  getTagBadgeOptions(tag: string): BadgeOptions {
    const badgeOption: BadgeOptions = {
      type: 'ROUNDEDTAG',
      displayKey: tag,
      displayName: tag,
      displayType: 'roundedTags',
      extraClass: 'text-base'
    };
    return badgeOption;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.customerId?.firstChange === false) {
      this.getCustomerDetails();
      this.changeDetectorRef.markForCheck();
    }
  }
}
